import { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Save, Eye, Info, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

// Tempo mínimo de exibição por slide. Pesquisas de DOOH apontam ~12–20s pra
// leitura confortável de uma headline + sub + preço; padronizamos 15s pra
// evitar "flash" onde o cliente nem tem tempo de processar a oferta.
export const MIN_SLIDE_DURATION_MS = 15000;
const MAX_SLIDE_DURATION_S = 600;
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { MediaUploader } from '@/components/ui/MediaUploader';
import { PlaylistPreviewModal } from '@/components/preview/PlaylistPreviewModal';
import { SlideRenderer } from '@/components/preview/SlideRenderer';
import { apiClient } from '@/services/api';
import { ScheduleEditor } from '@/components/playlists/ScheduleEditor';
import { parseSchedule, serializeSchedule, type SlideSchedule } from '@/lib/slideSchedule';
import { ImageSearchModal } from '../products/ImageSearchModal';

type Orientation = 'landscape' | 'portrait';

export function SlideEditor({ slideId, themeId, orientation = 'landscape' }: {
  slideId: number;
  themeId: number | null;
  orientation?: Orientation;
}) {
  const qc = useQueryClient();
  const [content, setContent] = useState<any>({});
  const [name, setName] = useState('');
  const [duration, setDuration] = useState(5000);
  const [schedule, setSchedule] = useState<SlideSchedule | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  // Carrega slide via cache da playlist
  const slide = qc.getQueryCache()
    .findAll({ queryKey: ['playlist'] })
    .map(q => q.state.data as any)
    .find((p: any) => p?.slides?.some((s: any) => s.id === slideId))
    ?.slides.find((s: any) => s.id === slideId);

  useEffect(() => {
    if (slide) {
      setName(slide.name || '');
      setDuration(slide.duration_ms);
      setSchedule(parseSchedule(slide.schedule));
      try { setContent(JSON.parse(slide.content)); } catch { setContent({}); }
    }
  }, [slideId, slide]);

  const updateMut = useMutation({
    mutationFn: () => apiClient.updateSlide(slideId, {
      name,
      // Garante o mínimo no envio — defesa em profundidade caso o input
      // tenha sido contornado (ex.: edição via console).
      duration_ms: Math.max(MIN_SLIDE_DURATION_MS, duration),
      content: JSON.stringify(content),
      // string vazia = limpar agendamento (null no PUT significa "não mexer")
      schedule: serializeSchedule(schedule) ?? '',
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['playlist'] }); toast.success('Slide salvo'); },
  });

  if (!slide) return <div>Slide não encontrado</div>;

  // Promo banner usa layout duas colunas com preview ao vivo — precisa de mais largura.
  const wide = slide.type === 'promo_banner';
  const isVideo = slide.type === 'video';

  return (
    <div className={`${wide ? 'max-w-6xl' : 'max-w-4xl'} mx-auto space-y-4`}>
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">{slide.type.replace(/_/g, ' ')}</h2>
        <div className="flex items-center gap-2">
          <Button onClick={() => setPreviewOpen(true)} variant="outline">
            <Eye className="h-4 w-4" />Pré-visualizar
          </Button>
          <Button onClick={() => updateMut.mutate()} loading={updateMut.isPending} variant="gradient">
            <Save className="h-4 w-4" />Salvar
          </Button>
        </div>
      </div>

      {/* Comum a todos */}
      <Card>
        <CardHeader><CardTitle>Geral</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Nome (opcional)</label>
              <Input value={name} onChange={e => setName(e.target.value)} />
            </div>
            <div>
              <label className="text-sm font-medium">Duração (segundos)</label>
              <Input
                type="number"
                min={MIN_SLIDE_DURATION_MS / 1000}
                max={isVideo ? undefined : MAX_SLIDE_DURATION_S}
                value={duration / 1000}
                onChange={e => {
                  // Permite digitar livremente — clamp acontece no blur
                  // pra não atrapalhar o usuário enquanto edita.
                  const v = Number(e.target.value);
                  setDuration(Number.isFinite(v) ? Math.max(0, v) * 1000 : duration);
                }}
                onBlur={() => {
                  if (duration < MIN_SLIDE_DURATION_MS) {
                    setDuration(MIN_SLIDE_DURATION_MS);
                    toast.info(`Duração mínima é ${MIN_SLIDE_DURATION_MS / 1000}s — ajustamos pra você.`);
                  } else if (!isVideo && duration > MAX_SLIDE_DURATION_S * 1000) {
                    setDuration(MAX_SLIDE_DURATION_S * 1000);
                  }
                }}
              />
              <p className="text-xs text-muted-foreground mt-1">
                {isVideo
                  ? `Mínimo ${MIN_SLIDE_DURATION_MS / 1000}s · sem limite máximo`
                  : `Mínimo ${MIN_SLIDE_DURATION_MS / 1000}s · máximo ${MAX_SLIDE_DURATION_S}s`}
              </p>
            </div>
          </div>
          <ReadingStrategyTips slideType={slide.type} duration={duration} />
          <ScheduleEditor value={schedule} onChange={setSchedule} />
        </CardContent>
      </Card>

      {/* Editor por tipo */}
      <Card>
        <CardHeader><CardTitle>Conteúdo</CardTitle></CardHeader>
        <CardContent>
          {slide.type === 'image' && <ImageEditor content={content} onChange={setContent} orientation={orientation} />}
          {slide.type === 'video' && <VideoEditor content={content} onChange={setContent} orientation={orientation} onDetectedDuration={(sec: number) => setDuration(Math.round(sec) * 1000)} />}
          {slide.type === 'price_table' && <PriceTableEditor content={content} onChange={setContent} themeId={themeId} />}
          {slide.type === 'promo_banner' && <PromoBannerEditor content={content} onChange={setContent} orientation={orientation} />}
          {slide.type === 'html' && <HtmlEditor content={content} onChange={setContent} />}
          {slide.type === 'weather' && <WeatherEditor content={content} onChange={setContent} />}
          {slide.type === 'datetime' && <DateTimeEditor content={content} onChange={setContent} />}
          {slide.type === 'football' && <FootballEditor content={content} onChange={setContent} />}
          {slide.type === 'product_carousel' && <ProductCarouselEditor content={content} onChange={setContent} />}
        </CardContent>
      </Card>

      {previewOpen && (
        <PlaylistPreviewModal
          slides={[{ type: slide.type, content, duration_ms: duration, name: name || slide.type }]}
          title={name || `Pré-visualizar ${slide.type.replace(/_/g, ' ')}`}
          orientation={orientation}
          onClose={() => setPreviewOpen(false)}
        />
      )}
    </div>
  );
}

// Card de recomendação de duração + estratégia de leitura.
// Mostrado em todo slide — explica em quanto tempo cliente consegue ler o
// conteúdo proposto e ajusta a recomendação ao tipo (vídeo, tabela, banner…).
function ReadingStrategyTips({ slideType, duration }: { slideType: string; duration: number }) {
  // Recomendações por tipo, baseadas em práticas de Digital Out-Of-Home (DOOH):
  // - Banner promocional: 15–20s (cliente em movimento, lê o headline e preço)
  // - Tabela de preços: 25–40s (precisa varrer várias linhas)
  // - Vídeo: usa a própria duração do vídeo
  // - Imagem: 15–20s (foto institucional/oferta)
  // - Clima/data: 10–15s (info passiva, glance)
  const rec = (() => {
    switch (slideType) {
      case 'promo_banner':
        return { ideal: '15 a 20s', why: '1 headline + 1 preço + 1 CTA. O cliente que passa próximo lê em 3-5s; quem para, em 12s.' };
      case 'price_table':
      case 'product_carousel':
        return { ideal: '25 a 40s', why: 'Várias linhas pra varrer. Calcule ~2s por produto visível — uma grade de 12 produtos pede no mínimo 25s.' };
      case 'video':
        return { ideal: 'igual ao vídeo', why: 'Detectamos a duração automaticamente. Vídeo nunca deve cortar — se for muito longo, edite a peça antes.' };
      case 'image':
        return { ideal: '15 a 20s', why: 'Imagem institucional ou oferta com pouco texto. Mais que 20s vira monotonia.' };
      case 'html':
        return { ideal: '20 a 30s', why: 'HTML custom geralmente tem animação. Dê tempo do ciclo completar 1x antes da troca.' };
      case 'weather':
      case 'datetime':
        return { ideal: '10 a 15s', why: 'Informação contextual (passiva). Cliente confere de relance — não precisa de muito tempo.' };
      case 'football':
        return { ideal: '30 a 60s', why: 'Placares e tabelas pedem leitura mais demorada. No modo "Alternar painéis", dê tempo de mostrar 2–3 painéis (ao vivo, tabela, próximos).' };
      default:
        return { ideal: '15 a 20s', why: 'Tempo padrão pra leitura confortável de uma mensagem curta.' };
    }
  })();

  const seconds = Math.round(duration / 1000);
  // Avalia se a duração atual está num bom range pro tipo de conteúdo.
  const tooShort = seconds < 15;
  const tooLong = (slideType === 'price_table' || slideType === 'product_carousel') ? seconds < 25 : seconds > 60;
  const warn = tooShort || tooLong;

  return (
    <div className={`rounded-lg border p-3 flex gap-3 ${
      warn
        ? 'border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-700'
        : 'border-sky-200 bg-sky-50 dark:bg-sky-950/30 dark:border-sky-800'
    }`}>
      <div className={`shrink-0 rounded-full h-8 w-8 grid place-items-center ${
        warn ? 'bg-amber-200 text-amber-900' : 'bg-sky-200 text-sky-900'
      }`}>
        <Info className="h-4 w-4" />
      </div>
      <div className="min-w-0 space-y-1.5">
        <div className={`text-sm font-semibold ${
          warn ? 'text-amber-900 dark:text-amber-200' : 'text-sky-900 dark:text-sky-200'
        }`}>
          Estratégia de leitura — recomendação: <span className="font-bold">{rec.ideal}</span>
        </div>
        <p className={`text-xs leading-snug ${
          warn ? 'text-amber-800 dark:text-amber-200/90' : 'text-sky-800 dark:text-sky-200/90'
        }`}>
          {rec.why}
        </p>
        <ul className="text-xs text-muted-foreground space-y-0.5 mt-1.5">
          <li>• <strong>3 segundos</strong> é o "glance" — só dá pra mostrar uma palavra-chave grande.</li>
          <li>• <strong>8–12 segundos</strong> permite ler uma headline + sub + preço (foco de oferta).</li>
          <li>• <strong>20+ segundos</strong> serve pra conteúdo que pede varredura (lista de preços, vídeo curto).</li>
          <li>• Repetição importa mais que tempo — playlist curta com loop &gt; playlist longa.</li>
        </ul>
        {tooShort && (
          <p className="text-xs font-semibold text-amber-900 dark:text-amber-200 mt-1">
            ⚠ {seconds}s é pouco — cliente nem termina de ler. Mínimo recomendado: 15s.
          </p>
        )}
        {tooLong && !tooShort && (
          <p className="text-xs font-semibold text-amber-900 dark:text-amber-200 mt-1">
            {slideType === 'price_table' || slideType === 'product_carousel'
              ? `⚠ ${seconds}s pode ser pouco pra uma tabela — considere 25s+.`
              : `⚠ ${seconds}s é longo demais — o conteúdo vira "papel de parede". Considere encurtar.`}
          </p>
        )}
      </div>
    </div>
  );
}

function ImageEditor({ content, onChange, orientation = 'landscape' }: any) {
  const fit: 'contain' | 'cover' = content.fit === 'cover' ? 'cover' : 'contain';
  const isPortrait = orientation === 'portrait';

  return (
    <div className="grid lg:grid-cols-[1fr_minmax(0,360px)] gap-6">
      {/* Esquerda — controles */}
      <div className="space-y-4 min-w-0">
        <div>
          <label className="text-sm font-medium block mb-1">Imagem</label>
          <MediaUploader
            kind="image"
            value={content.url}
            onChange={(url) => onChange({ ...content, url })}
          />
        </div>
        <div>
          <label className="text-sm font-medium">Ou cole uma URL</label>
          <Input value={content.url || ''} onChange={e => onChange({ ...content, url: e.target.value })} placeholder="https://..." />
        </div>
        <div>
          <label className="text-sm font-medium">Encaixe</label>
          <select value={fit} onChange={e => onChange({ ...content, fit: e.target.value })} className="h-10 rounded-md border border-input bg-background px-3 text-sm w-full">
            <option value="contain">Encaixar (mostra a imagem inteira, sem cortar)</option>
            <option value="cover">Preencher (preenche a tela, pode cortar bordas)</option>
          </select>
          <p className="text-xs text-muted-foreground mt-1">
            "Encaixar" garante que nada seja cortado. "Preencher" usa toda a tela mas pode cortar topo/laterais quando a imagem não tem a proporção da TV.
          </p>
        </div>

        <label className="flex items-center justify-between gap-3 cursor-pointer">
          <div>
            <span className="text-sm font-medium block">Rodapé de notícias + relógio</span>
            <span className="text-xs text-muted-foreground">Faixa no rodapé passando as últimas notícias e a hora certa, por cima da imagem</span>
          </div>
          <input
            type="checkbox"
            className="w-4 h-4 accent-brand-600"
            checked={!!content.news_ticker}
            onChange={e => onChange({ ...content, news_ticker: e.target.checked })}
          />
        </label>

        {/* Aviso explícito sobre orientação — só aparece em portrait + imagem horizontal,
            que é o caso típico do usuário (foto 16:9 em TV em pé). */}
        {isPortrait && content.url && (
          <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-700 p-3 text-sm">
            <div className="font-semibold text-amber-900 dark:text-amber-200 mb-0.5">
              ⚠️ Playlist em modo vertical (9:16)
            </div>
            <div className="text-amber-800 dark:text-amber-200/90 text-xs leading-snug">
              {fit === 'cover'
                ? 'Imagens horizontais serão cortadas nas laterais. Use uma arte vertical (1080×1920) para preencher sem perda.'
                : 'Imagens horizontais aparecerão pequenas, no meio, com tarjas pretas em cima e embaixo. Para preencher a TV inteira, troque para uma arte vertical (1080×1920).'}
            </div>
          </div>
        )}
      </div>

      {/* Direita — preview com proporção real da TV.
          Frame com bezel preto + base imitando uma TV para o usuário enxergar
          imediatamente o que vai cortar / aparecer com tarja. */}
      <div className="lg:sticky lg:top-4 self-start space-y-2">
        <div className="text-xs font-semibold uppercase text-muted-foreground">
          Como vai aparecer na TV ({isPortrait ? '1080×1920' : '1920×1080'})
        </div>
        <TvFrame orientation={orientation}>
          {content.url ? (
            <img
              src={content.url}
              alt=""
              className="w-full h-full"
              style={{ objectFit: fit, display: 'block' }}
            />
          ) : (
            <div className="w-full h-full grid place-items-center text-white/40 text-xs">
              Adicione uma imagem
            </div>
          )}
        </TvFrame>
        <p className="text-xs text-muted-foreground">
          Preview em escala — proporção exata da TV {isPortrait ? 'em pé' : 'deitada'}.
        </p>
      </div>
    </div>
  );
}

// Frame de TV com a proporção certa (16:9 ou 9:16) e bezel preto, simulando o
// monitor físico — facilita o usuário identificar tarjas/cortes na hora.
function TvFrame({ orientation = 'landscape', children }: { orientation?: 'landscape' | 'portrait'; children: React.ReactNode }) {
  const isPortrait = orientation === 'portrait';
  return (
    <div className="w-full flex flex-col items-center">
      <div
        className="bg-black rounded-md shadow-md ring-1 ring-black/20 overflow-hidden"
        style={{
          aspectRatio: isPortrait ? '9 / 16' : '16 / 9',
          width: isPortrait ? '60%' : '100%',
          maxHeight: isPortrait ? 520 : undefined,
        }}
      >
        <div className="w-full h-full bg-black">{children}</div>
      </div>
      {/* Pedestal pra reforçar visualmente que é uma TV */}
      <div className="h-1.5 w-10 bg-muted-foreground/30 rounded-b-md" />
      <div className="h-0.5 w-20 bg-muted-foreground/20 rounded-full mt-0.5" />
    </div>
  );
}

function VideoEditor({ content, onChange, onDetectedDuration, orientation = 'landscape' }: any) {
  const initialUrl = useRef<string | null>(content.url ?? null);
  const lastProbedUrl = useRef<string | null>(content.url ?? null);

  useEffect(() => {
    const url = content.url;
    if (!url) { lastProbedUrl.current = null; return; }
    if (lastProbedUrl.current === url) return;
    lastProbedUrl.current = url;
    if (url === initialUrl.current) return;

    const v = document.createElement('video');
    v.preload = 'metadata';
    v.muted = true;
    let cancelled = false;
    const cleanup = () => {
      v.onloadedmetadata = null;
      v.onerror = null;
      v.src = '';
    };
    v.onloadedmetadata = () => {
      if (cancelled) return;
      const sec = v.duration;
      if (Number.isFinite(sec) && sec > 0) onDetectedDuration?.(sec);
      cleanup();
    };
    v.onerror = cleanup;
    v.src = url;

    return () => { cancelled = true; cleanup(); };
  }, [content.url, onDetectedDuration]);

  const isPortrait = orientation === 'portrait';
  return (
    <div className="grid lg:grid-cols-[1fr_minmax(0,360px)] gap-6">
      <div className="space-y-4 min-w-0">
        <div>
          <label className="text-sm font-medium block mb-1">Vídeo</label>
          <MediaUploader
            kind="video"
            value={content.url}
            onChange={(url) => onChange({ ...content, url })}
            hint="MP4/WebM até 30 MB. Para TVs com internet ruim, prefira clipes curtos (5–30s) em 1–2 Mbps."
          />
        </div>
        <div>
          <label className="text-sm font-medium">Ou cole uma URL</label>
          <Input value={content.url || ''} onChange={e => onChange({ ...content, url: e.target.value })} placeholder="https://...mp4" />
        </div>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={content.mute ?? true} onChange={e => onChange({ ...content, mute: e.target.checked })} />Mudo</label>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={content.loop ?? false} onChange={e => onChange({ ...content, loop: e.target.checked })} />Loop interno (continua tocando)</label>

        {isPortrait && content.url && (
          <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-700 p-3 text-xs">
            <div className="font-semibold text-amber-900 dark:text-amber-200 mb-0.5">
              ⚠️ Playlist em modo vertical
            </div>
            <div className="text-amber-800 dark:text-amber-200/90 leading-snug">
              Vídeos horizontais aparecerão pequenos com tarjas em cima/embaixo. Para preencher a TV em pé, exporte o vídeo em 9:16 (1080×1920).
            </div>
          </div>
        )}
      </div>

      <div className="lg:sticky lg:top-4 self-start space-y-2">
        <div className="text-xs font-semibold uppercase text-muted-foreground">
          Como vai aparecer na TV ({isPortrait ? '1080×1920' : '1920×1080'})
        </div>
        <TvFrame orientation={orientation}>
          {content.url ? (
            <video
              src={content.url}
              className="w-full h-full"
              style={{ objectFit: 'contain', display: 'block', background: '#000' }}
              muted
              autoPlay
              loop
              playsInline
            />
          ) : (
            <div className="w-full h-full grid place-items-center text-white/40 text-xs">
              Adicione um vídeo
            </div>
          )}
        </TvFrame>
        <p className="text-xs text-muted-foreground">
          Preview muto em loop — proporção real da TV {isPortrait ? 'em pé' : 'deitada'}.
        </p>
      </div>
    </div>
  );
}

function ThemedColorPicker({ label, value, defaultValue, onChange }: {
  label: string;
  value: string | undefined;
  defaultValue: string;
  onChange: (v: string) => void;
}) {
  const using = value && value !== defaultValue;
  return (
    <div>
      <label className="text-xs text-muted-foreground">{label}</label>
      <Input
        type="color"
        value={value || defaultValue}
        onChange={(e) => onChange(e.target.value)}
        className="h-9 p-1"
      />
      <div className="flex items-center justify-between mt-1 gap-2">
        <span className="text-[10px] text-muted-foreground truncate">
          {using ? 'Personalizado' : 'Vindo do tema'}
        </span>
        {using && (
          <button
            type="button"
            onClick={() => onChange('')}
            className="text-[10px] text-brand-700 hover:underline whitespace-nowrap"
          >
            usar do tema
          </button>
        )}
      </div>
    </div>
  );
}

function PriceTableEditor({ content, onChange, themeId }: any) {
  const isFeatured = content.layout === 'featured';
  const { data: managedCategories = [] } = useQuery({
    queryKey: ['product-categories-managed'],
    queryFn: () => apiClient.getProductCategoriesManaged().then(r => r.data as { nome: string }[]),
  });
  const { data: availableDepartamentos = [] } = useQuery({
    queryKey: ['product-departments'],
    queryFn: () => apiClient.getProductDepartments().then(r => r.data as string[]),
  });
  const { data: tenant } = useQuery({
    queryKey: ['tenant'],
    queryFn: () => apiClient.getTenant().then(r => r.data),
    enabled: isFeatured,
  });
  const { data: themes = [] } = useQuery({
    queryKey: ['themes'],
    queryFn: () => apiClient.getThemes().then(r => r.data),
    enabled: isFeatured,
  });
  // Defaults herdados do tenant/tema — mostrados como placeholder quando o usuário não sobrescreveu.
  const tenantBranding = (() => {
    try { return tenant?.branding ? (typeof tenant.branding === 'string' ? JSON.parse(tenant.branding) : tenant.branding) : {}; }
    catch { return {}; }
  })();
  const selectedTheme = themes.find((t: any) => t.id === themeId);
  const themeColors = (() => {
    try {
      const cfg = selectedTheme?.config
        ? (typeof selectedTheme.config === 'string' ? JSON.parse(selectedTheme.config) : selectedTheme.config)
        : {};
      return cfg.colors ?? {};
    } catch { return {}; }
  })();
  const defaultLogo: string | undefined = tenantBranding.logo_url;
  const defaultHeaderColor: string = themeColors.primary ?? '#7C2D12';
  const defaultTextColor: string = themeColors.background ?? '#FFFFFF';
  const defaultAccentColor: string = themeColors.accent ?? themeColors.secondary ?? '#D7A86E';

  // Migra do legado `filter.categoria` (string) → `filter.categorias` (array)
  const selectedCategorias: string[] = Array.isArray(content.filter?.categorias)
    ? content.filter.categorias.filter((c: any) => typeof c === 'string' && c.trim())
    : (content.filter?.categoria ? [content.filter.categoria] : []);
  const [categoriaInput, setCategoriaInput] = useState('');

  const setCategorias = (next: string[]) => {
    const { categoria: _legacy, categorias: _curr, ...restFilter } = content.filter || {};
    onChange({
      ...content,
      filter: { ...restFilter, ...(next.length > 0 ? { categorias: next } : {}) },
    });
  };

  const addCategoria = (raw: string) => {
    const v = raw.trim();
    if (!v) return;
    if (selectedCategorias.some(c => c.toLowerCase() === v.toLowerCase())) {
      setCategoriaInput('');
      return;
    }
    // Se o usuário digitou algo que casa (case-insensitive) com uma categoria gerenciada,
    // usa o nome canônico do banco — evita divergência com o `categoria` dos produtos.
    const canonical = managedCategories.find(c => c.nome.toLowerCase() === v.toLowerCase());
    setCategorias([...selectedCategorias, canonical ? canonical.nome : v]);
    setCategoriaInput('');
  };

  const removeCategoria = (c: string) => {
    setCategorias(selectedCategorias.filter(x => x !== c));
  };

  const availableCategorias = managedCategories.filter(
    c => !selectedCategorias.some(s => s.toLowerCase() === c.nome.toLowerCase()),
  );

  // Departamentos — mesmo padrão de chips
  const selectedDepartamentos: string[] = Array.isArray(content.filter?.departamentos)
    ? content.filter.departamentos.filter((d: any) => typeof d === 'string' && d.trim())
    : (content.filter?.departamento ? [content.filter.departamento] : []);
  const [departamentoInput, setDepartamentoInput] = useState('');

  const setDepartamentos = (next: string[]) => {
    const { departamento: _legacy, departamentos: _curr, ...restFilter } = content.filter || {};
    onChange({ ...content, filter: { ...restFilter, ...(next.length ? { departamentos: next } : {}) } });
  };

  const addDepartamento = (raw: string) => {
    const v = raw.trim();
    if (!v) return;
    if (selectedDepartamentos.some(d => d.toLowerCase() === v.toLowerCase())) {
      setDepartamentoInput('');
      return;
    }
    const canonical = availableDepartamentos.find(d => d.toLowerCase() === v.toLowerCase());
    setDepartamentos([...selectedDepartamentos, canonical ?? v]);
    setDepartamentoInput('');
  };

  const removeDepartamento = (d: string) => {
    setDepartamentos(selectedDepartamentos.filter(x => x !== d));
  };

  const departamentoSuggestions = availableDepartamentos.filter(
    d => !selectedDepartamentos.some(s => s.toLowerCase() === d.toLowerCase()),
  );

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-sm font-medium">Layout</label>
          <select className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" value={content.layout || 'grid_3'} onChange={e => onChange({ ...content, layout: e.target.value })}>
            <option value="grid_3">Grade 3 colunas</option>
            <option value="grid_4">Grade 4 colunas</option>
            <option value="list">Lista</option>
            <option value="featured">Destaque split (lista + 1 grande rotativo)</option>
            <option value="letterboard">Placar de aeroporto (2 colunas, split-flap)</option>
            <option value="menuboard">Letreiro de lanchonete (placa preta, texto amarelo)</option>
          </select>
        </div>
        <div>
          <label className="text-sm font-medium">Limite de produtos {isFeatured && <span className="text-xs text-muted-foreground font-normal">(total — paginado)</span>}</label>
          <Input type="number" min={1} max={200} value={content.limit || 12} onChange={e => onChange({ ...content, limit: Number(e.target.value) })} />
        </div>
      </div>

      {isFeatured && (
        <div className="rounded-md border border-input bg-muted/30 p-3 space-y-3">
          <div className="text-xs font-semibold uppercase text-muted-foreground">Cabeçalho do destaque</div>
          <div className="grid grid-cols-2 gap-3">
            <Input placeholder="Título esquerdo (ex: Padaria)" value={content.left_title || ''} onChange={e => onChange({ ...content, left_title: e.target.value })} />
            <Input placeholder="Título direito (ex: Ofertas)" value={content.right_title || ''} onChange={e => onChange({ ...content, right_title: e.target.value })} />
          </div>
          <Input placeholder="Subtítulo / slogan (opcional)" value={content.subtitle || ''} onChange={e => onChange({ ...content, subtitle: e.target.value })} />
          <div>
            <label className="text-xs text-muted-foreground">Logo central</label>
            {!content.logo_url && defaultLogo ? (
              <div className="rounded-md border border-dashed border-input bg-muted/30 p-3 flex items-center gap-3">
                <img src={defaultLogo} alt="Logo da empresa" className="h-12 w-12 object-contain rounded bg-white border" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">Usando o logo da empresa</div>
                  <div className="text-xs text-muted-foreground">Configurado em Configurações → Marca. Você pode substituir só pra este slide abaixo.</div>
                </div>
                <button
                  type="button"
                  onClick={() => onChange({ ...content, logo_url: ' ' })}
                  className="text-xs font-medium text-brand-700 hover:underline whitespace-nowrap"
                >
                  Substituir
                </button>
              </div>
            ) : (
              <MediaUploader
                kind="logo"
                value={content.logo_url && content.logo_url.trim() ? content.logo_url : undefined}
                onChange={(url) => onChange({ ...content, logo_url: url })}
                hint="Arraste a logo ou clique pra enviar — PNG/SVG transparente, até 5 MB"
              />
            )}
            {content.logo_url && defaultLogo && (
              <button
                type="button"
                onClick={() => onChange({ ...content, logo_url: '' })}
                className="text-xs text-muted-foreground hover:text-foreground mt-2"
              >
                ↺ Voltar ao logo padrão da empresa
              </button>
            )}
          </div>
          <div className="grid grid-cols-3 gap-3">
            <ThemedColorPicker
              label="Cor do header"
              value={content.header_color}
              defaultValue={defaultHeaderColor}
              onChange={(v) => onChange({ ...content, header_color: v })}
            />
            <ThemedColorPicker
              label="Cor do texto"
              value={content.header_text_color}
              defaultValue={defaultTextColor}
              onChange={(v) => onChange({ ...content, header_text_color: v })}
            />
            <ThemedColorPicker
              label="Detalhe (acento)"
              value={content.accent_color}
              defaultValue={defaultAccentColor}
              onChange={(v) => onChange({ ...content, accent_color: v })}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-muted-foreground">Tempo entre destaques (ms)</label>
              <Input type="number" min={1000} max={30000} step={500} value={content.featured_rotation_ms || 5000} onChange={e => onChange({ ...content, featured_rotation_ms: Number(e.target.value) })} />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Produtos por página da lista</label>
              <Input type="number" min={1} max={20} value={content.list_page_size || 10} onChange={e => onChange({ ...content, list_page_size: Number(e.target.value) })} />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">A lista da esquerda pagina automaticamente. O tempo de cada página = duração do slide ÷ nº de páginas. Se não preencher logo/cores, herda do tema da playlist.</p>
        </div>
      )}

      {/* Opções específicas do layout Lista */}
      {content.layout === 'list' && (
        <div className="rounded-md border border-input bg-muted/30 p-3 space-y-3">
          <div className="text-xs font-semibold uppercase text-muted-foreground">Opções da lista (2 colunas)</div>
          <label className="flex items-center justify-between gap-3 cursor-pointer">
            <div>
              <span className="text-sm font-medium block">Zebrado — alternar cor de fundo</span>
              <span className="text-xs text-muted-foreground">Linhas pares e ímpares com cores diferentes</span>
            </div>
            <input
              type="checkbox"
              className="w-4 h-4 accent-brand-600"
              checked={content.list_zebra ?? false}
              onChange={e => onChange({ ...content, list_zebra: e.target.checked })}
            />
          </label>
          {content.list_zebra && (
            <div className="grid grid-cols-2 gap-3">
              <ThemedColorPicker
                label="Linhas pares"
                value={content.list_zebra_color_even}
                defaultValue="#f1f5f9"
                onChange={v => onChange({ ...content, list_zebra_color_even: v })}
              />
              <ThemedColorPicker
                label="Linhas ímpares"
                value={content.list_zebra_color_odd}
                defaultValue="#ffffff"
                onChange={v => onChange({ ...content, list_zebra_color_odd: v })}
              />
            </div>
          )}
        </div>
      )}

      <div className="space-y-2">
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={content.filter?.only_promo ?? false} onChange={e => onChange({ ...content, filter: { ...content.filter, only_promo: e.target.checked } })} />Apenas em promoção</label>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={content.show_image ?? true} onChange={e => onChange({ ...content, show_image: e.target.checked })} />Mostrar imagens</label>
      </div>
      <div className="space-y-1.5">
        <label className="text-sm font-medium">Filtrar por categoria (opcional)</label>
        <div
          className="flex flex-wrap items-center gap-1.5 min-h-10 w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 cursor-text"
          onClick={(e) => {
            const target = e.target as HTMLElement;
            if (target.tagName !== 'INPUT' && target.tagName !== 'BUTTON') {
              (e.currentTarget.querySelector('input') as HTMLInputElement | null)?.focus();
            }
          }}
        >
          {selectedCategorias.map(c => (
            <span
              key={c}
              className="inline-flex items-center gap-1 rounded-md bg-brand-100 border border-brand-300 text-foreground px-2 py-0.5 text-xs font-medium"
            >
              {c}
              <button
                type="button"
                onClick={() => removeCategoria(c)}
                className="hover:bg-brand-300/60 rounded-full w-4 h-4 inline-flex items-center justify-center leading-none text-muted-foreground -mr-0.5"
                aria-label={`Remover ${c}`}
              >
                ×
              </button>
            </span>
          ))}
          <input
            list="price-table-categorias-list"
            value={categoriaInput}
            onChange={e => {
              const val = e.target.value;
              // Se selecionou via datalist (match case-insensitive com sugestão), adiciona com nome canônico.
              const exact = availableCategorias.find(c => c.nome.toLowerCase() === val.toLowerCase());
              if (exact) addCategoria(exact.nome);
              else setCategoriaInput(val);
            }}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ',') {
                e.preventDefault();
                addCategoria(categoriaInput);
              } else if (e.key === 'Backspace' && !categoriaInput && selectedCategorias.length > 0) {
                removeCategoria(selectedCategorias[selectedCategorias.length - 1]);
              }
            }}
            onBlur={() => addCategoria(categoriaInput)}
            className="flex-1 min-w-[160px] bg-transparent outline-none border-0 px-1 py-0.5 text-sm"
            placeholder={selectedCategorias.length === 0 ? 'Selecione uma ou mais categorias' : 'Adicionar outra…'}
          />
        </div>
        <datalist id="price-table-categorias-list">
          {availableCategorias.map(c => <option key={c.nome} value={c.nome} />)}
        </datalist>
      </div>
      <div className="space-y-1.5">
        <label className="text-sm font-medium">Filtrar por departamento (opcional)</label>
        <div
          className="flex flex-wrap items-center gap-1.5 min-h-10 w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm cursor-text focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-0"
          onClick={e => (e.currentTarget.querySelector('input') as HTMLInputElement | null)?.focus()}
        >
          {selectedDepartamentos.map(d => (
            <span
              key={d}
              className="inline-flex items-center gap-1 rounded-md bg-brand-100 border border-brand-300 text-foreground px-2 py-0.5 text-xs font-medium"
            >
              {d}
              <button
                type="button"
                onClick={() => removeDepartamento(d)}
                className="hover:bg-brand-300/60 rounded-full w-4 h-4 inline-flex items-center justify-center leading-none text-muted-foreground -mr-0.5"
                aria-label={`Remover ${d}`}
              >
                ×
              </button>
            </span>
          ))}
          <input
            list="price-table-departamentos-list"
            value={departamentoInput}
            onChange={e => {
              const val = e.target.value;
              const exact = departamentoSuggestions.find(d => d.toLowerCase() === val.toLowerCase());
              if (exact) addDepartamento(exact);
              else setDepartamentoInput(val);
            }}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ',') {
                e.preventDefault();
                addDepartamento(departamentoInput);
              } else if (e.key === 'Backspace' && !departamentoInput && selectedDepartamentos.length > 0) {
                removeDepartamento(selectedDepartamentos[selectedDepartamentos.length - 1]);
              }
            }}
            onBlur={() => addDepartamento(departamentoInput)}
            className="flex-1 min-w-[160px] bg-transparent outline-none border-0 px-1 py-0.5 text-sm"
            placeholder={selectedDepartamentos.length === 0 ? 'Selecione um ou mais departamentos' : 'Adicionar outro…'}
          />
        </div>
        <datalist id="price-table-departamentos-list">
          {departamentoSuggestions.map(d => <option key={d} value={d} />)}
        </datalist>
      </div>
    </div>
  );
}

const PROMO_TEMPLATES: { id: string; label: string; desc: string }[] = [
  { id: 'spotlight', label: 'Spotlight',     desc: 'Foco central com selo de preço' },
  { id: 'split',     label: 'Split',         desc: 'Imagem ou ícone à esquerda, texto à direita' },
  { id: 'urgency',   label: 'Urgência',      desc: 'Contagem regressiva e ação imediata' },
  { id: 'minimal',   label: 'Minimalista',   desc: 'Tipografia leve e bastante respiro' },
  { id: 'bold',      label: 'Impacto',       desc: 'Headline gigante com selo lateral' },
  { id: 'vitrine',   label: 'Vitrine',       desc: 'Foto cinematográfica do produto e preço no canto' },
];

const PROMO_PRESETS: { name: string; bg: string; text: string; accent: string }[] = [
  { name: 'Vermelho clássico',  bg: '#C81E1E', text: '#FFFFFF', accent: '#FFD700' },
  { name: 'Preto premium',      bg: '#0F172A', text: '#FFFFFF', accent: '#F59E0B' },
  { name: 'Verde mercado',      bg: '#15803D', text: '#FFFFFF', accent: '#FACC15' },
  { name: 'Laranja oferta',     bg: '#EA580C', text: '#FFFFFF', accent: '#FFFFFF' },
  { name: 'Azul corporativo',   bg: '#1D4ED8', text: '#FFFFFF', accent: '#FBBF24' },
  { name: 'Rosa',               bg: '#DB2777', text: '#FFFFFF', accent: '#FEF3C7' },
  { name: 'Branco minimal',     bg: '#F8FAFC', text: '#0F172A', accent: '#DC2626' },
];

const PROMO_ICONS: { id: string; emoji: string; label: string }[] = [
  { id: 'fire',     emoji: '🔥', label: 'Fogo' },
  { id: 'star',     emoji: '⭐', label: 'Estrela' },
  { id: 'gift',     emoji: '🎁', label: 'Presente' },
  { id: 'meat',     emoji: '🥩', label: 'Açougue' },
  { id: 'bread',    emoji: '🥖', label: 'Padaria' },
  { id: 'coffee',   emoji: '☕', label: 'Café' },
  { id: 'cart',     emoji: '🛒', label: 'Carrinho' },
  { id: 'sparkles', emoji: '✨', label: 'Brilho' },
];

function ScaledPreview({ children, orientation = 'landscape' }: { children: React.ReactNode; orientation?: 'landscape' | 'portrait' }) {
  const ref = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0.3);
  const isPortrait = orientation === 'portrait';
  const logicalW = isPortrait ? 1080 : 1920;
  const logicalH = isPortrait ? 1920 : 1080;
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const recalc = () => {
      const w = el.getBoundingClientRect().width;
      setScale(w > 0 ? w / logicalW : 0.3);
    };
    const ro = new ResizeObserver(recalc);
    ro.observe(el);
    recalc();
    return () => ro.disconnect();
  }, [logicalW]);
  return (
    <div
      ref={ref}
      className="bg-black rounded-lg overflow-hidden ring-1 ring-black/10 shadow-md"
      style={{
        aspectRatio: isPortrait ? '9 / 16' : '16 / 9',
        width: isPortrait ? '60%' : '100%',
        marginLeft: isPortrait ? 'auto' : undefined,
        marginRight: isPortrait ? 'auto' : undefined,
      }}
    >
      <div style={{ width: logicalW, height: logicalH, transform: `scale(${scale})`, transformOrigin: 'top left' }}>
        {children}
      </div>
    </div>
  );
}

function toLocalInput(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16);
}

function PromoBannerEditor({ content, onChange, orientation = 'landscape' }: any) {
  const set = (patch: any) => onChange({ ...content, ...patch });
  const [imageSearchTarget, setImageSearchTarget] = useState<'product' | 'background' | null>(null);

  const isAuto = content.mode === 'auto';
  const timePP = Math.max(5, Math.round((content.auto_time_per_product_ms || 10000) / 1000));

  const { data: categories = [] } = useQuery({
    queryKey: ['slide-editor-categories'],
    queryFn: () => apiClient.getProductCategories().then(r => Array.isArray(r.data) ? r.data as string[] : []),
  });

  const { data: promoProducts = [] } = useQuery<any[]>({
    queryKey: ['slide-editor-promo-products', content.auto_categoria],
    queryFn: () => apiClient.getProducts({ promoOnly: true, ...(content.auto_categoria ? { categoria: content.auto_categoria } : {}), limit: 50 }).then(r => r.data?.items || []),
    enabled: isAuto,
  });

  // Mapeia valores legados ("classic" → split, "modern" → spotlight) para o id atual.
  const rawTemplate = String(content.template ?? 'spotlight');
  const template = rawTemplate === 'classic' ? 'split'
                 : rawTemplate === 'modern'  ? 'spotlight'
                 : PROMO_TEMPLATES.some(t => t.id === rawTemplate) ? rawTemplate
                 : 'spotlight';
  const isUrgency = template === 'urgency';
  const overlay = typeof content.background_overlay === 'number' ? content.background_overlay : 35;

  return (
    <div className="grid lg:grid-cols-[1fr_minmax(0,420px)] gap-6">
      {/* COLUNA ESQUERDA — formulário */}
      <div className="space-y-5 min-w-0">
        {/* Modo: Manual vs Automático */}
        <div className="flex rounded-lg border border-input overflow-hidden text-sm font-medium">
          <button type="button" onClick={() => set({ mode: 'manual' })}
            className={`flex-1 py-2 transition-colors ${!isAuto ? 'bg-brand-600 text-white' : 'bg-background text-muted-foreground hover:bg-muted'}`}>
            Manual
          </button>
          <button type="button" onClick={() => set({ mode: 'auto' })}
            className={`flex-1 py-2 transition-colors flex items-center justify-center gap-1.5 ${isAuto ? 'bg-brand-600 text-white' : 'bg-background text-muted-foreground hover:bg-muted'}`}>
            <Sparkles className="h-3.5 w-3.5" />
            Automático
          </button>
        </div>

        {/* Campos do modo automático */}
        {isAuto && (
          <div className="space-y-3 rounded-md border border-brand-200 bg-brand-50/40 p-3">
            <div className="text-xs font-semibold uppercase text-brand-700">Produtos em promoção</div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Categoria (opcional — vazio = todas)</label>
              <select
                value={content.auto_categoria || ''}
                onChange={e => set({ auto_categoria: e.target.value || '' })}
                className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="">Todas as categorias</option>
                {categories.map((c: any) => (
                  <option key={typeof c === 'string' ? c : c.nome} value={typeof c === 'string' ? c : c.nome}>
                    {typeof c === 'string' ? c : c.nome}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Tempo por produto (segundos)</label>
              <Input
                type="number"
                min={5}
                max={60}
                value={timePP}
                onChange={e => set({ auto_time_per_product_ms: Math.max(5, Number(e.target.value)) * 1000 })}
              />
            </div>
            {promoProducts.length > 0 ? (
              <p className="text-xs text-brand-700 bg-brand-100 rounded px-2 py-1.5">
                <strong>{promoProducts.length}</strong> produto(s) em promoção → {promoProducts.length} × {timePP}s = <strong>{promoProducts.length * timePP}s</strong> por passada
              </p>
            ) : (
              <p className="text-xs text-muted-foreground">Nenhum produto em promoção{content.auto_categoria ? ' nessa categoria' : ''}.</p>
            )}
          </div>
        )}

        {/* Modelo */}
        <div className="space-y-2">
          <label className="text-sm font-semibold">Modelo</label>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {PROMO_TEMPLATES.map(t => (
              <button
                key={t.id}
                type="button"
                onClick={() => set({ template: t.id })}
                className={`text-left rounded-lg border-2 p-3 transition-all hover:border-brand-400 ${template === t.id ? 'border-brand-500 bg-brand-50/60' : 'border-input bg-background'}`}
              >
                <div className="font-semibold text-sm">{t.label}</div>
                <div className="text-xs text-muted-foreground mt-0.5 leading-tight">{t.desc}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Texto — só no modo manual */}
        {!isAuto && <div className="space-y-3 rounded-md border border-input bg-muted/30 p-3">
          <div className="text-xs font-semibold uppercase text-muted-foreground">Texto</div>
          <div>
            <label className="text-xs text-muted-foreground">Headline (chamada principal)</label>
            <Input placeholder="Ex: OFERTA DA SEMANA" value={content.headline || ''} onChange={e => set({ headline: e.target.value })} />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Subtítulo</label>
            <Input placeholder="Ex: Picanha premium até sábado" value={content.subtitle || ''} onChange={e => set({ subtitle: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs text-muted-foreground">Preço (texto livre)</label>
              <Input placeholder="R$ 4,99" value={content.price || ''} onChange={e => set({ price: e.target.value })} />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">De (preço antigo, opcional)</label>
              <Input placeholder="R$ 8,99" value={content.price_old || ''} onChange={e => set({ price_old: e.target.value })} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs text-muted-foreground">Selo de validade</label>
              <Input placeholder="Válido até 31/12" value={content.validity || ''} onChange={e => set({ validity: e.target.value })} />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Botão de ação (CTA)</label>
              <Input placeholder="Aproveite hoje!" value={content.cta || ''} onChange={e => set({ cta: e.target.value })} />
            </div>
          </div>
        </div>}

        {/* CTA no modo automático */}
        {isAuto && (
          <div className="space-y-1 rounded-md border border-input bg-muted/30 p-3">
            <div className="text-xs font-semibold uppercase text-muted-foreground">Chamada de ação</div>
            <label className="text-xs text-muted-foreground">Botão de ação (CTA)</label>
            <Input placeholder="Aproveite hoje!" value={content.cta || ''} onChange={e => set({ cta: e.target.value })} />
          </div>
        )}

        {/* Ícone */}
        <div className="space-y-2">
          <label className="text-sm font-semibold">Ícone</label>
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={() => set({ icon: '' })}
              className={`px-3 h-10 rounded-md border text-xs font-medium ${!content.icon ? 'border-brand-500 bg-brand-50/60' : 'border-input'}`}>
              Sem ícone
            </button>
            {PROMO_ICONS.map(i => (
              <button key={i.id} type="button" onClick={() => set({ icon: i.id })}
                title={i.label}
                className={`w-10 h-10 rounded-md border text-xl grid place-items-center transition-colors hover:border-brand-400 ${content.icon === i.id ? 'border-brand-500 bg-brand-50/60' : 'border-input'}`}>
                {i.emoji}
              </button>
            ))}
          </div>
        </div>

        {/* Cores */}
        <div className="space-y-3 rounded-md border border-input bg-muted/30 p-3">
          <div className="flex items-center justify-between">
            <div className="text-xs font-semibold uppercase text-muted-foreground">Cores</div>
            <div className="text-xs text-muted-foreground">Paletas prontas</div>
          </div>
          <div className="flex flex-wrap gap-2">
            {PROMO_PRESETS.map(preset => {
              const isActive =
                content.background_color === preset.bg &&
                content.text_color === preset.text &&
                content.accent_color === preset.accent;
              return (
                <button
                  key={preset.name}
                  type="button"
                  title={preset.name}
                  onClick={() => set({ background_color: preset.bg, text_color: preset.text, accent_color: preset.accent })}
                  className={`flex items-center gap-1.5 h-9 rounded-md border bg-background pl-1.5 pr-2.5 text-xs font-medium transition-all hover:border-brand-400 ${isActive ? 'border-brand-500 ring-1 ring-brand-300' : 'border-input'}`}
                >
                  <span className="flex">
                    <span className="block h-5 w-5 rounded-l border border-black/10" style={{ backgroundColor: preset.bg }} />
                    <span className="block h-5 w-5 border-y border-black/10" style={{ backgroundColor: preset.text }} />
                    <span className="block h-5 w-5 rounded-r border border-black/10" style={{ backgroundColor: preset.accent }} />
                  </span>
                  {preset.name}
                </button>
              );
            })}
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-muted-foreground">Fundo</label>
              <Input type="color" value={content.background_color || '#C81E1E'} onChange={e => set({ background_color: e.target.value })} className="h-10 p-1 cursor-pointer" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Texto</label>
              <Input type="color" value={content.text_color || '#FFFFFF'} onChange={e => set({ text_color: e.target.value })} className="h-10 p-1 cursor-pointer" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Destaque (preço)</label>
              <Input type="color" value={content.accent_color || '#FFD700'} onChange={e => set({ accent_color: e.target.value })} className="h-10 p-1 cursor-pointer" />
            </div>
          </div>
        </div>

        {/* Imagem & Logo */}
        <div className="space-y-3 rounded-md border border-input bg-muted/30 p-3">
          <div className="text-xs font-semibold uppercase text-muted-foreground">Imagem & Logo</div>
          {!isAuto && (
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs text-muted-foreground">Foto do produto (exibida em destaque no banner)</label>
              <button
                type="button"
                onClick={() => setImageSearchTarget('product')}
                className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-md bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white shadow-sm hover:shadow-md hover:from-violet-600 hover:to-fuchsia-600 transition-all"
              >
                <Sparkles className="h-3.5 w-3.5" />
                Buscar imagens
              </button>
            </div>
            <MediaUploader
              kind="image"
              value={content.product_image_url}
              onChange={(url) => set({ product_image_url: url })}
              hint="PNG/JPG, até 5 MB — aparece grande no layout"
            />
          </div>
          )}
          {isAuto && (
            <p className="text-xs text-muted-foreground bg-muted/60 rounded px-2 py-1.5">
              Foto do produto: usada automaticamente da ficha de cada produto em promoção.
            </p>
          )}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs text-muted-foreground">Imagem de fundo (opcional)</label>
              <button
                type="button"
                onClick={() => setImageSearchTarget('background')}
                className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-md bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white shadow-sm hover:shadow-md hover:from-violet-600 hover:to-fuchsia-600 transition-all"
              >
                <Sparkles className="h-3.5 w-3.5" />
                Buscar imagens
              </button>
            </div>
            <MediaUploader
              kind="image"
              value={content.background_url}
              onChange={(url) => set({ background_url: url })}
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Ou cole uma URL</label>
            <Input placeholder="https://..." value={content.background_url || ''} onChange={e => set({ background_url: e.target.value })} />
          </div>
          {content.background_url && (
            <div>
              <label className="text-xs text-muted-foreground flex items-center justify-between">
                <span>Escurecer imagem com a cor de fundo</span>
                <span className="font-mono text-foreground">{overlay}%</span>
              </label>
              <input type="range" min={0} max={100} value={overlay} onChange={e => set({ background_overlay: Number(e.target.value) })} className="w-full" />
              <p className="text-xs text-muted-foreground">0% = imagem pura · 100% = só a cor sólida (oculta a foto).</p>
            </div>
          )}
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!content.show_logo} onChange={e => set({ show_logo: e.target.checked })} />
            Mostrar logo da loja no canto
          </label>
        </div>

        {/* Countdown — só urgency manual */}
        {isUrgency && !isAuto && (
          <div className="space-y-2 rounded-md border border-input bg-muted/30 p-3">
            <div className="text-xs font-semibold uppercase text-muted-foreground">Contagem regressiva</div>
            <Input
              type="datetime-local"
              value={toLocalInput(content.countdown_to ?? '')}
              onChange={e => set({ countdown_to: e.target.value ? new Date(e.target.value).toISOString() : '' })}
            />
            <p className="text-xs text-muted-foreground">A contagem aparece no banner. Ao chegar em 00:00 fica zerada — o slide continua tocando até você atualizar o conteúdo.</p>
          </div>
        )}
      </div>

      {/* COLUNA DIREITA — preview ao vivo */}
      <div className="lg:sticky lg:top-4 self-start space-y-2">
        <div className="text-xs font-semibold uppercase text-muted-foreground">Pré-visualização ao vivo</div>
        <ScaledPreview orientation={orientation}>
          <SlideRenderer type="promo_banner" content={content} />
        </ScaledPreview>
        <p className="text-xs text-muted-foreground">
          {orientation === 'portrait'
            ? 'Tela cheia 1080×1920 (9:16 — TV em pé). Use "Pré-visualizar" para abrir em tela cheia.'
            : 'Tela cheia 1920×1080 (16:9 — TV deitada). Use "Pré-visualizar" para abrir em tela cheia.'}
        </p>
      </div>

      {imageSearchTarget && (
        <ImageSearchModal
          initialQuery={
            isAuto
              ? (content.auto_categoria || 'promoção')
              : [content.headline, content.subtitle].filter(Boolean).join(' ').trim()
          }
          onSelect={(url) => {
            set(imageSearchTarget === 'product' ? { product_image_url: url } : { background_url: url });
            setImageSearchTarget(null);
          }}
          onClose={() => setImageSearchTarget(null)}
        />
      )}
    </div>
  );
}

function HtmlEditor({ content, onChange }: any) {
  return (
    <div className="space-y-2">
      <label className="text-sm font-medium">HTML custom</label>
      <textarea
        value={content.html || ''}
        onChange={e => onChange({ ...content, html: e.target.value })}
        className="w-full h-64 rounded-md border border-input bg-background p-3 font-mono text-xs"
        placeholder="<div>...</div>"
      />
      <div className="text-xs text-muted-foreground">Variáveis: {`{{date}}`}, {`{{time}}`}, {`{{tenant_name}}`}</div>
    </div>
  );
}

function WeatherEditor({ content, onChange }: any) {
  return (
    <div className="space-y-4">
      <div>
        <Input placeholder="Cidade (ex: São Paulo, Rio de Janeiro)" value={content.city || ''} onChange={e => onChange({ ...content, city: e.target.value })} />
        <p className="text-xs text-muted-foreground mt-1">Dados meteorológicos via Open-Meteo (sem chave). Cache de 30 min.</p>
      </div>
      <div>
        <select className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" value={content.layout || 'compact'} onChange={e => onChange({ ...content, layout: e.target.value })}>
          <option value="compact">Compacto — clima atual em destaque</option>
          <option value="full">Completo — clima atual + previsão 4 dias</option>
          <option value="animated">Animado — cenário cinematográfico com efeitos</option>
        </select>
      </div>
      <label className="flex items-center justify-between gap-3 cursor-pointer">
        <div>
          <span className="text-sm font-medium block">Mostrar relógio (hora atual)</span>
          <span className="text-xs text-muted-foreground">Exibe hora ao vivo e data em pt-BR junto ao clima</span>
        </div>
        <input
          type="checkbox"
          className="w-4 h-4 accent-brand-600"
          checked={content.show_clock !== false}
          onChange={e => onChange({ ...content, show_clock: e.target.checked })}
        />
      </label>
    </div>
  );
}

function FootballEditor({ content, onChange }: any) {
  const set = (key: string, val: any) => onChange({ ...content, [key]: val });
  const view = content.view || 'rotate';
  const theme = content.theme || 'dark';

  const VIEWS: { value: string; label: string; desc: string }[] = [
    { value: 'rotate', label: 'Alternar painéis', desc: 'Mostra ao vivo, tabela e próximos em rotação' },
    { value: 'live_today', label: 'Ao vivo / hoje', desc: 'Placares dos jogos de hoje, com destaque pros que estão rolando' },
    { value: 'results', label: 'Resultados de ontem', desc: 'Jogos encerrados no dia anterior' },
    { value: 'standings', label: 'Classificação', desc: 'Tabela completa da Série A, com zonas de Libertadores e rebaixamento' },
    { value: 'upcoming', label: 'Próximos jogos', desc: 'Agenda dos próximos confrontos' },
  ];

  return (
    <div className="space-y-5">
      <div className="rounded-lg border border-sky-200 bg-sky-50 dark:bg-sky-950/30 dark:border-sky-800 p-3">
        <p className="text-xs text-sky-900 dark:text-sky-200 leading-snug">
          🏆 Dados ao vivo do <strong>Brasileirão Série A</strong> via football-data.org. Placares e
          classificação são atualizados automaticamente — você não precisa cadastrar nada.
        </p>
      </div>

      {/* Painel */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">O que exibir</p>
        <div className="grid grid-cols-2 gap-2">
          {VIEWS.map(opt => (
            <button
              key={opt.value}
              type="button"
              onClick={() => set('view', opt.value)}
              className={`text-left rounded-lg border-2 py-2.5 px-3 transition-all ${view === opt.value ? 'border-brand-600 bg-brand-50/60 shadow-sm' : 'border-border hover:border-brand-300'}`}
            >
              <span className={`text-sm font-semibold block ${view === opt.value ? 'text-brand-700' : 'text-foreground'}`}>{opt.label}</span>
              <span className="text-xs text-muted-foreground">{opt.desc}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Tema visual */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Tema</p>
        <div className="grid grid-cols-3 gap-2">
          {([
            { value: 'dark',  label: 'Escuro',  bg: '#0b1220', color: '#fff' },
            { value: 'green', label: 'Gramado', bg: 'linear-gradient(135deg,#064e3b,#065f46)', color: '#fff' },
            { value: 'brand', label: 'Marca',   bg: 'linear-gradient(135deg,#7c3aed,#4f46e5)', color: '#fff' },
          ] as const).map(opt => (
            <button
              key={opt.value}
              type="button"
              onClick={() => set('theme', opt.value)}
              className={`rounded-lg border-2 py-3 px-2 flex flex-col items-center gap-2 transition-all ${theme === opt.value ? 'border-brand-600 shadow-sm' : 'border-border hover:border-brand-300'}`}
            >
              <span className="h-8 w-full rounded" style={{ background: opt.bg }} />
              <span className="text-xs text-muted-foreground">{opt.label}</span>
            </button>
          ))}
        </div>
      </div>

      <label className="flex items-center justify-between gap-3 cursor-pointer">
        <div>
          <span className="text-sm font-medium block">Mostrar relógio (hora atual)</span>
          <span className="text-xs text-muted-foreground">Exibe hora ao vivo e data em pt-BR no canto</span>
        </div>
        <input
          type="checkbox"
          className="w-4 h-4 accent-brand-600"
          checked={content.show_clock !== false}
          onChange={e => set('show_clock', e.target.checked)}
        />
      </label>
    </div>
  );
}

function DateTimeEditor({ content, onChange }: any) {
  const { data: tenant } = useQuery({
    queryKey: ['tenant'],
    queryFn: () => apiClient.getTenant().then(r => r.data),
  });

  const companyLogoUrl: string | null = (() => {
    try {
      const b = typeof tenant?.branding === 'string' ? JSON.parse(tenant.branding || '{}') : (tenant?.branding || {});
      return b.logo_url || null;
    } catch { return null; }
  })();

  const set = (key: string, val: any) => onChange({ ...content, [key]: val });

  const style = content.style || 'large';
  const theme = content.theme || 'dark';
  const showDate = content.show_date !== false;
  const showSeconds = content.show_seconds !== false;
  const showLogo = content.show_logo === true;
  const logoSource = content.logo_source || 'company';

  return (
    <div className="space-y-5">

      {/* Tamanho */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Tamanho do relógio</p>
        <div className="grid grid-cols-3 gap-2">
          {([
            { value: 'large',   label: 'Grande',  preview: '12:34', sz: 'text-2xl' },
            { value: 'compact', label: 'Médio',   preview: '12:34', sz: 'text-xl'  },
            { value: 'minimal', label: 'Mínimo',  preview: '12:34', sz: 'text-base' },
          ] as const).map(opt => (
            <button
              key={opt.value}
              type="button"
              onClick={() => set('style', opt.value)}
              className={`rounded-lg border-2 py-3 px-2 flex flex-col items-center gap-1.5 transition-all ${style === opt.value ? 'border-brand-600 bg-brand-50/60 shadow-sm' : 'border-border hover:border-brand-300'}`}
            >
              <span className={`font-bold tabular-nums leading-none ${opt.sz} ${style === opt.value ? 'text-brand-700' : 'text-foreground'}`}>{opt.preview}</span>
              <span className="text-xs text-muted-foreground">{opt.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Tema visual */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Tema</p>
        <div className="grid grid-cols-4 gap-2">
          {([
            { value: 'dark',  label: 'Escuro', bg: '#0f172a', color: '#fff' },
            { value: 'light', label: 'Claro',  bg: '#f8fafc', color: '#1e293b' },
            { value: 'glass', label: 'Glass',  bg: 'linear-gradient(135deg,#1e293b,#334155)', color: '#fff' },
            { value: 'brand', label: 'Marca',  bg: 'linear-gradient(135deg,#7c3aed,#4f46e5)', color: '#fff' },
          ] as const).map(opt => (
            <button
              key={opt.value}
              type="button"
              onClick={() => set('theme', opt.value)}
              style={{ background: opt.bg, color: opt.color }}
              className={`rounded-lg border-2 py-2.5 text-xs font-semibold transition-all ${theme === opt.value ? 'border-brand-400 ring-2 ring-brand-300/40 scale-[1.04]' : 'border-transparent hover:border-brand-300'}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Conteúdo */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Conteúdo</p>
        <div className="space-y-1.5">
          {([
            { key: 'show_date',    label: 'Mostrar data por extenso', checked: showDate },
            { key: 'show_seconds', label: 'Mostrar segundos',         checked: showSeconds },
          ] as const).map(item => (
            <label key={item.key} className="flex items-center justify-between gap-3 rounded-lg border border-border px-3 py-2.5 cursor-pointer hover:bg-muted/30 transition-colors">
              <span className="text-sm">{item.label}</span>
              <input
                type="checkbox"
                className="w-4 h-4 accent-brand-600 cursor-pointer"
                checked={item.checked}
                onChange={e => set(item.key, e.target.checked)}
              />
            </label>
          ))}
        </div>
      </div>

      {/* Logomarca */}
      <div>
        <label className="flex items-center justify-between gap-3 rounded-lg border border-border px-3 py-2.5 cursor-pointer hover:bg-muted/30 transition-colors">
          <div>
            <span className="text-sm font-medium block">Exibir logomarca</span>
            <span className="text-xs text-muted-foreground">Aparece acima do relógio</span>
          </div>
          <input
            type="checkbox"
            className="w-4 h-4 accent-brand-600 cursor-pointer"
            checked={showLogo}
            onChange={e => set('show_logo', e.target.checked)}
          />
        </label>

        {showLogo && (
          <div className="mt-2 rounded-lg border border-brand-200 bg-brand-50/30 p-3 space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => set('logo_source', 'company')}
                className={`rounded-md border-2 py-2 px-3 text-xs font-medium transition-all ${logoSource === 'company' ? 'border-brand-600 bg-brand-50 text-brand-700' : 'border-border hover:border-brand-300 text-muted-foreground'}`}
              >
                Da empresa
              </button>
              <button
                type="button"
                onClick={() => set('logo_source', 'custom')}
                className={`rounded-md border-2 py-2 px-3 text-xs font-medium transition-all ${logoSource === 'custom' ? 'border-brand-600 bg-brand-50 text-brand-700' : 'border-border hover:border-brand-300 text-muted-foreground'}`}
              >
                Personalizada
              </button>
            </div>

            {logoSource === 'company' && (
              <div className="rounded-md bg-white/70 border border-border flex items-center justify-center min-h-[56px] p-3">
                {companyLogoUrl
                  ? <img src={companyLogoUrl} alt="Logomarca da empresa" className="max-h-10 max-w-full object-contain" />
                  : <p className="text-xs text-muted-foreground text-center">Sem logomarca cadastrada. Configure em <strong>Configurações → Empresa</strong>.</p>
                }
              </div>
            )}

            {logoSource === 'custom' && (
              <MediaUploader
                kind="logo"
                value={content.logo_url || ''}
                onChange={url => set('logo_url', url)}
                hint="PNG transparente recomendado"
              />
            )}
          </div>
        )}
      </div>

    </div>
  );
}

function ProductCarouselEditor({ content, onChange }: any) {
  const set = (key: string, val: any) => onChange({ ...content, [key]: val });

  const itemsVisible = content.items_visible || 6;
  const limitTotal   = content.limit || 30;
  const speedMs      = content.transition_speed_ms || 5000;

  const GRID_OPTIONS = [
    { value: 3, label: '3 produtos', sub: '1 linha × 3 cols' },
    { value: 6, label: '6 produtos', sub: '2 linhas × 3 cols' },
    { value: 9, label: '9 produtos', sub: '3 linhas × 3 cols' },
  ] as const;

  const SPEED_OPTIONS = [
    { value: 3000,  label: '3s',  sub: 'Rápido'     },
    { value: 5000,  label: '5s',  sub: 'Normal'      },
    { value: 8000,  label: '8s',  sub: 'Lento'       },
    { value: 15000, label: '15s', sub: 'Muito lento' },
  ] as const;

  return (
    <div className="space-y-6">

      {/* Produtos por tela */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">Produtos por tela</p>
        <p className="text-xs text-muted-foreground mb-2.5">O carrossel avança automaticamente para mostrar os próximos</p>
        <div className="grid grid-cols-3 gap-2">
          {GRID_OPTIONS.map(opt => {
            const rows = opt.value / 3;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => set('items_visible', opt.value)}
                className={`rounded-lg border-2 py-3 px-2 flex flex-col items-center gap-2 transition-all ${itemsVisible === opt.value ? 'border-brand-600 bg-brand-50/60' : 'border-border hover:border-brand-300'}`}
              >
                <div className="flex flex-col gap-0.5">
                  {Array.from({ length: rows }).map((_, r) => (
                    <div key={r} className="flex gap-0.5">
                      {Array.from({ length: 3 }).map((_, c) => (
                        <div key={c} className={`w-3 h-2.5 rounded-[2px] ${itemsVisible === opt.value ? 'bg-brand-500' : 'bg-muted-foreground/35'}`} />
                      ))}
                    </div>
                  ))}
                </div>
                <div className="text-center">
                  <div className={`text-xs font-semibold ${itemsVisible === opt.value ? 'text-brand-700' : ''}`}>{opt.label}</div>
                  <div className="text-[10px] text-muted-foreground">{opt.sub}</div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Tempo por tela */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2.5">Tempo por tela</p>
        <div className="grid grid-cols-4 gap-2">
          {SPEED_OPTIONS.map(opt => (
            <button
              key={opt.value}
              type="button"
              onClick={() => set('transition_speed_ms', opt.value)}
              className={`rounded-lg border-2 py-2.5 flex flex-col items-center gap-0.5 transition-all ${speedMs === opt.value ? 'border-brand-600 bg-brand-50/60' : 'border-border hover:border-brand-300'}`}
            >
              <span className={`text-sm font-bold ${speedMs === opt.value ? 'text-brand-700' : ''}`}>{opt.label}</span>
              <span className="text-[10px] text-muted-foreground">{opt.sub}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Total na rotação */}
      <div>
        <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground block mb-1">
          Total de produtos na rotação
        </label>
        <p className="text-xs text-muted-foreground mb-2">Quantos produtos diferentes serão exibidos no total (o carrossel percorre todos)</p>
        <Input
          type="number"
          min={itemsVisible}
          max={200}
          value={limitTotal}
          onChange={e => {
            const v = Math.max(itemsVisible, Number(e.target.value));
            set('limit', v);
          }}
        />
      </div>

    </div>
  );
}
